<template>
  <div
    :data-schema-id="schema.id"
    :data-field-index="depth > 1 ? path[path.length - 1] : undefined"
    :data-depth="depth"
  >
    <template v-for="(field, fieldIndex) in fields" :key="field.name">
      <div :class="depth <= 2 ? 'sortableRow' : ''">
        <div class="field-name d-flex align-center" :style="{ paddingLeft: indent }">
          <v-btn
            :class="depth <= 2 ? 'border-none cursor-handle sortHandle' : 'border-0'"
            size="small"
            variant="plain"
            tabindex="-1"
            icon
            :disabled="isWorkflowDisabled || depth > 2"
          >
            <v-icon
              :icon="
                field.type === 'filter' ? 'mdi-filter-variant'
                : field.computed ? 'mdi-function-variant'
                  : 'mds:drag-indicator'
              "
            />
            <v-icon
              v-if="field.type === 'filter' && field.computed"
              class="handle-computed-badge"
              icon="mdi-function-variant"
              size="10"
            />
          </v-btn>
          <v-tooltip
            :disabled="!field.description"
            :open-on-focus="false"
            location="top"
            open-delay="300"
            max-width="280"
          >
            <template v-slot:activator="{ props: tooltipProps }">
              <quick-rename-input
                v-bind="tooltipProps"
                :value="field.name"
                @onChange="(e) => onBlurField(e, fieldIndex)"
                density="compact"
                :max-length="30"
                class="flex-grow-1"
                :disabled="isWorkflowDisabled"
              />
            </template>
            <span>{{ field.description }}</span>
          </v-tooltip>
          <v-chip
            v-if="field.relatedEntityId && getRelatedEntity(field)"
            density="compact"
            size="small"
            :closable="!isWorkflowDisabled && !isMaxDepth"
            class="related-entity-chip px-2 ml-1"
          >
            {{ field.cardinality === 'one-to-many'
              ? `[ ${getRelatedEntity(field)?.name} ]`
              : `${getRelatedEntity(field)?.name}` }}
            <template v-if="!isWorkflowDisabled && !isMaxDepth" #close>
              <v-icon
                icon="mdi-close-circle"
                color="#888"
                :size="16"
                class="remove-entity-icon"
                @click.stop="onSelectRelatedEntity(null, fieldIndex)"
              />
            </template>
          </v-chip>
          <query-field-actions
            v-if="!isWorkflowDisabled"
            :field="field"
            :hide-entity="isMaxDepth"
            @toggle-filter="toggleFieldFilterAtPath(schema.id, currentPath(fieldIndex))"
            @toggle-computed="toggleFieldComputedAtPath(schema.id, currentPath(fieldIndex))"
            @select-entity="(entity) => onSelectRelatedEntity(entity, fieldIndex)"
            @delete="removeSchemaFieldAtPath(schema.id, currentPath(fieldIndex))"
            @update:cardinality="(cardinality) => setSchemaFieldCardinalityAtPath(schema.id, currentPath(fieldIndex), cardinality)"
          />
        </div>
        <div
          v-if="field.relatedEntityId && !isMaxDepth"
          :data-schema-id="schema.id"
          :data-field-index="depth === 1 ? fieldIndex : undefined"
        >
          <read-model-field-rows
            :fields="field.fields || []"
            :depth="depth + 1"
            :maxDepth="maxDepth"
            :path="currentPath(fieldIndex)"
            :schema="schema"
            :isWorkflowDisabled="isWorkflowDisabled"
          />
          <div
            v-if="!isWorkflowDisabled"
            class="d-flex align-center btn-add-sub-field"
            :style="{ paddingLeft: childIndent }"
          >
            <v-tooltip location="bottom" open-delay="100">
              <template v-slot:activator="{ on, props: btnProps }">
                <v-btn
                  v-bind="btnProps"
                  v-on="{ ...on }"
                  size="small"
                  variant="plain"
                  @mousedown="addSchemaFieldAtPath(schema.id, currentPath(fieldIndex))"
                  tabindex="-1"
                  icon="mdi-plus"
                />
              </template>
              <span>Add field</span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import QuickRenameInput from '@/components/QuickRenameInput'
import QueryFieldActions from '@/components/QueryFieldActions.vue'
import useDomainModel from '@/composables/useDomainModel'
import useReadModelActions from '@/composables/useReadModelActions'

const props = defineProps({
  fields: { type: Array, default: () => [] },
  depth: { type: Number, default: 1 },
  maxDepth: { type: Number, default: 3 },
  path: { type: Array, default: () => [] },
  schema: { type: Object, required: true },
  isWorkflowDisabled: { type: Boolean, default: false }
})

const store = useStore()

const {
  renameSchemaFieldAtPath,
  removeSchemaFieldAtPath,
  selectRelatedEntityAtSchemaPath,
  setSchemaFieldCardinalityAtPath,
  addSchemaFieldAtPath
} = useDomainModel()

const { toggleFieldFilterAtPath, toggleFieldComputedAtPath } = useReadModelActions()

const workflow = computed(() => store.state.workflow.workflow)

const isMaxDepth = computed(() => props.depth >= props.maxDepth)
const indent = computed(() => (props.depth - 1) * 16 + 'px')
const childIndent = computed(() => props.depth * 16 + 'px')

const currentPath = (fieldIndex) => [...props.path, fieldIndex]

const getRelatedEntity = (field) => {
  if (!field.relatedEntityId) {
    return null
  }

  return (workflow.value.schemas ?? []).find((s) => s.id === field.relatedEntityId)
}

const onBlurField = (e, fieldIndex) => {
  const newName = e.target.innerText?.trim() ?? ''
  renameSchemaFieldAtPath(props.schema.id, currentPath(fieldIndex), newName)
}

const onSelectRelatedEntity = (entity, fieldIndex) => {
  selectRelatedEntityAtSchemaPath(entity, props.schema.id, currentPath(fieldIndex))
}
</script>

<style scoped>
.field-name {
  font-size: 13px;
}

.cursor-handle {
  cursor: grab;
}

.sortHandle {
  margin-left: -4px;
}

.handle-computed-badge {
  position: absolute;
  bottom: -1px;
  right: -1px;
  background: white;
  border-radius: 50%;
}

:deep(.query-field-actions-btn) {
  opacity: 0;
  transition: opacity 0.2s;
}

.sortableRow:hover :deep(.query-field-actions-btn) {
  opacity: 0.7;
}

.sortableRow:hover :deep(.query-field-actions-btn:hover) {
  opacity: 1;
}

.btn-add-sub-field {
  opacity: 0;
  height: 0;
  transition: all 0.2s;
}

.sortableRow:hover > div > .btn-add-sub-field {
  opacity: 1;
  height: 16px;
}

.sortableRow:hover > div > .btn-add-sub-field .v-btn {
  opacity: 0.7;
}

.sortableRow:hover > div > .btn-add-sub-field .v-btn:hover {
  opacity: 1;
}

:deep(.related-entity-chip) {
  position: relative;

  &&& .v-chip__close {
    margin-inline-start: 0;
    margin-inline-end: 0;
    position: absolute;
    right: 0;
    background: #e8e8e8;
    opacity: 0;
    transition: all 0.2s;
  }

  &:hover {
    &&& .v-chip__close {
      opacity: 1;
    }
  }
}
</style>
